"use client";

import React, { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";

interface ButtonProps {
  children: ReactNode;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  icon?: ReactNode;
  iconRight?: boolean;
  size?: "sm" | "md" | "lg";
}

function sizeClasses(size: ButtonProps["size"] = "md") {
  switch (size) {
    case "sm":
      return "px-4 py-2 text-sm rounded-full";
    case "lg":
      return "px-7 py-3.5 text-base rounded-full";
    default:
      return "px-5 py-2.5 text-sm rounded-full";
  }
}

export function PrimaryButton({
  children,
  className,
  href,
  target,
  rel,
  onClick,
  icon,
  iconRight = true,
  size = "md"
}: ButtonProps) {
  const ButtonContent = (
    <>
      {icon && !iconRight && <span className="mr-2">{icon}</span>}
      <span className="relative z-10">{children}</span>
      {icon && iconRight && <span className="ml-2 relative z-10">{icon}</span>}
    </>
  );

  const buttonClasses = cn(
    "relative overflow-hidden font-medium inline-flex items-center justify-center transition-all duration-300",
    "text-white shadow-glow-sm hover:shadow-glow",
    "bg-gradient-to-br from-brand-700 via-brand-800 to-brand-900",
    "ring-1 ring-inset ring-white/10",
    sizeClasses(size),
    className
  );

  const Inner = (
    <>
      <span className="shimmer" aria-hidden="true" />
      {ButtonContent}
    </>
  );

  if (href) {
    return (
      <motion.a
        href={href}
        target={target}
        rel={rel}
        className={buttonClasses}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
      >
        {Inner}
      </motion.a>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      className={buttonClasses}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
    >
      {Inner}
    </motion.button>
  );
}

export function SecondaryButton({
  children,
  className,
  href,
  target,
  rel,
  onClick,
  icon,
  iconRight = true,
  size = "md"
}: ButtonProps) {
  const { isDark } = useTheme();

  const ButtonContent = (
    <>
      {icon && !iconRight && <span className="mr-2">{icon}</span>}
      <span className="relative z-10">{children}</span>
      {icon && iconRight && <span className="ml-2 relative z-10">{icon}</span>}
    </>
  );

  const buttonClasses = cn(
    "relative overflow-hidden font-medium inline-flex items-center justify-center transition-all duration-300",
    "backdrop-blur-md",
    isDark
      ? "bg-white/[0.04] text-white border border-white/10 hover:bg-white/[0.08] hover:border-white/20"
      : "bg-white/60 text-stone-900 border border-stone-200 hover:bg-white hover:border-stone-300",
    sizeClasses(size),
    className
  );

  if (href) {
    return (
      <motion.a
        href={href}
        target={target}
        rel={rel}
        className={buttonClasses}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
      >
        {ButtonContent}
      </motion.a>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      className={buttonClasses}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
    >
      {ButtonContent}
    </motion.button>
  );
}

export function GhostButton({
  children,
  className,
  href,
  target,
  rel,
  onClick,
  icon,
  size = "md"
}: ButtonProps) {
  const { isDark } = useTheme();
  const buttonClasses = cn(
    "font-medium inline-flex items-center justify-center transition-all duration-200",
    isDark
      ? "text-white/70 hover:text-white"
      : "text-stone-700 hover:text-stone-900",
    sizeClasses(size),
    className
  );
  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={buttonClasses}>
        {icon && <span className="mr-2">{icon}</span>}
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={buttonClasses}>
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
}

export function ThemeToggleButton() {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className={isDark ? "text-white" : "text-stone-800"}
      aria-label="Toggle theme"
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
}

export function FloatingThemeToggle() {
  const { isDark, toggleTheme } = useTheme();

  return (
    <motion.button
      onClick={toggleTheme}
      aria-label="Toggle theme"
      className="fixed bottom-6 right-6 z-50 group"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.92 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
    >
      <div className="relative h-14 w-14">
        <span
          className="absolute inset-[-3px] rounded-full conic-ring opacity-80 group-hover:opacity-100 transition-opacity"
          style={{ filter: "blur(0.5px)" }}
        />
        <span
          className={cn(
            "absolute inset-[1px] rounded-full flex items-center justify-center",
            isDark
              ? "bg-[#0E0E14] text-white"
              : "bg-white text-stone-900"
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isDark ? (
              <motion.span
                key="sun"
                initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
                transition={{ duration: 0.25 }}
                className="flex items-center justify-center"
              >
                <Sun className="h-5 w-5" />
              </motion.span>
            ) : (
              <motion.span
                key="moon"
                initial={{ opacity: 0, rotate: 90, scale: 0.6 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: -90, scale: 0.6 }}
                transition={{ duration: 0.25 }}
                className="flex items-center justify-center"
              >
                <Moon className="h-5 w-5" />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
      </div>
    </motion.button>
  );
}
